/**
 * RichVendingClientDialog — v2.5.912
 *
 * The multi-tab enterprise client form: Identity / 6 Contacts / 2 Banking /
 * KYC / Commercial. Backed by /api/vending/enterprise/clients (RichClientPayload).
 *
 * Usage:
 *   <RichVendingClientDialog client={editing} onClose={close} onSaved={reload} />
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Trash2, Building2, Users, Landmark, ShieldCheck, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/vending`;

const CONTACT_KEYS = [
  { key: 'primary_contact',      label: 'Primary contact',     hint: 'Day-to-day operational contact' },
  { key: 'finance_contact_ap',   label: 'Finance — AP',        hint: 'Pays your invoices' },
  { key: 'finance_contact_ar',   label: 'Finance — AR',        hint: 'Receives your commission cheques' },
  { key: 'facilities_contact',   label: 'Facilities',          hint: 'Site access, keys, permits' },
  { key: 'decision_maker',       label: 'Decision maker',      hint: 'The person who signed the deal' },
  { key: 'escalation_contact',   label: 'Escalation',          hint: 'For urgent issues' },
];

function emptyContact() {
  return { full_name: '', role: '', email: '', mobile: '', landline: '', department: '', notes: '' };
}
function emptyBank() {
  return {
    account_holder: '', bank_name: '', branch: '', account_number: '',
    swift_bic: '', iban: '', routing_number: '', sort_code: '',
    payment_currency: 'PHP', payment_method: 'bank_transfer', payment_reference: '',
  };
}
function blankRichClient() {
  const c = {
    name: '', trading_name: '', industry: 'office', website: '',
    tax_id: '', reg_number: '', logo_url: '',
    hq_address_line1: '', hq_address_line2: '', hq_city: '', hq_region: '', hq_country: 'PH', hq_postal_code: '',
    commission_payout_details: emptyBank(),
    invoice_receipt_details: emptyBank(),
    billing_email: '', billing_model: 'paying_vend',
    credit_limit: 0, payment_terms_days: 30, preferred_currency: 'PHP',
    kyc_status: 'pending', kyc_notes: '',
    insurance_certificate_url: '',
    contract_documents: [],
    date_onboarded: new Date().toISOString().slice(0, 10),
    notes: '', tags: [],
  };
  CONTACT_KEYS.forEach(({ key }) => { c[key] = emptyContact(); });
  return c;
}

export default function RichVendingClientDialog({ client, onClose, onSaved }) {
  const [form, setForm] = useState({ ...blankRichClient(), ...client });
  const [tab, setTab] = useState('identity');
  const [busy, setBusy] = useState(false);
  const isNew = !client?.id;

  useEffect(() => { setForm({ ...blankRichClient(), ...client }); }, [client]);

  const patch = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const patchNested = (parent, k, v) => setForm(f => ({ ...f, [parent]: { ...(f[parent] || {}), [k]: v } }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error('Client name is required'); setTab('identity'); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      ['id', 'tenant_id', 'created_at', 'updated_at', 'created_by', '_id', 'is_demo'].forEach(k => delete payload[k]);
      if (isNew) {
        await axios.post(`${API}/enterprise/clients`, payload);
        toast.success('Enterprise client created');
      } else {
        await axios.patch(`${API}/enterprise/clients/${client.id}`, payload);
        toast.success('Client updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!client?.id) return;
    if (!window.confirm(`Delete "${client.name}"? Locations remain but become orphaned.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/clients/${client.id}`);
      toast.success('Client deleted');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto" data-testid="rich-client-dialog">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5 text-emerald-600" />
            {isNew ? 'New enterprise client' : `Edit — ${form.name}`}
            {form.kyc_status && !isNew && (
              <Badge variant="outline" className={`text-[10px] ${form.kyc_status === 'verified' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : form.kyc_status === 'expired' ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                KYC {form.kyc_status}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="space-y-3">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="identity" className="text-[10px] sm:text-xs" data-testid="tab-identity"><Building2 className="h-3 w-3 sm:mr-1" /><span className="hidden sm:inline">Identity</span></TabsTrigger>
            <TabsTrigger value="contacts" className="text-[10px] sm:text-xs" data-testid="tab-contacts"><Users className="h-3 w-3 sm:mr-1" /><span className="hidden sm:inline">Contacts</span></TabsTrigger>
            <TabsTrigger value="banking" className="text-[10px] sm:text-xs" data-testid="tab-banking"><Landmark className="h-3 w-3 sm:mr-1" /><span className="hidden sm:inline">Banking</span></TabsTrigger>
            <TabsTrigger value="kyc" className="text-[10px] sm:text-xs" data-testid="tab-kyc"><ShieldCheck className="h-3 w-3 sm:mr-1" /><span className="hidden sm:inline">KYC</span></TabsTrigger>
            <TabsTrigger value="commercial" className="text-[10px] sm:text-xs" data-testid="tab-commercial"><DollarSign className="h-3 w-3 sm:mr-1" /><span className="hidden sm:inline">Commercial</span></TabsTrigger>
          </TabsList>

          {/* IDENTITY */}
          <TabsContent value="identity" className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Field label="Legal name *"><Input value={form.name} onChange={e => patch('name', e.target.value)} data-testid="client-name-input" /></Field>
              <Field label="Trading / DBA name"><Input value={form.trading_name} onChange={e => patch('trading_name', e.target.value)} /></Field>
              <Field label="Industry">
                <Select value={form.industry} onValueChange={v => patch('industry', v)}>
                  <SelectTrigger data-testid="client-industry-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="office">Office</SelectItem>
                    <SelectItem value="hospital">Hospital</SelectItem>
                    <SelectItem value="factory">Factory</SelectItem>
                    <SelectItem value="school">School</SelectItem>
                    <SelectItem value="mall">Mall / retail</SelectItem>
                    <SelectItem value="hotel">Hotel</SelectItem>
                    <SelectItem value="transit">Transit</SelectItem>
                    <SelectItem value="gym">Gym</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Website"><Input value={form.website} onChange={e => patch('website', e.target.value)} placeholder="https://…" /></Field>
              <Field label="Tax ID / VAT"><Input value={form.tax_id} onChange={e => patch('tax_id', e.target.value)} /></Field>
              <Field label="Reg number (SEC / etc.)"><Input value={form.reg_number} onChange={e => patch('reg_number', e.target.value)} /></Field>
              <Field label="Logo URL"><Input value={form.logo_url} onChange={e => patch('logo_url', e.target.value)} /></Field>
              <Field label="Onboarded on"><Input type="date" value={form.date_onboarded || ''} onChange={e => patch('date_onboarded', e.target.value)} /></Field>
            </div>
            <Card className="bg-gray-50"><CardContent className="p-3 space-y-2">
              <div className="text-xs font-semibold uppercase text-gray-700">Registered address</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Field label="Address line 1"><Input value={form.hq_address_line1} onChange={e => patch('hq_address_line1', e.target.value)} /></Field>
                <Field label="Address line 2"><Input value={form.hq_address_line2} onChange={e => patch('hq_address_line2', e.target.value)} /></Field>
                <Field label="City"><Input value={form.hq_city} onChange={e => patch('hq_city', e.target.value)} /></Field>
                <Field label="Region / State"><Input value={form.hq_region} onChange={e => patch('hq_region', e.target.value)} /></Field>
                <Field label="Country (ISO)"><Input maxLength={2} value={form.hq_country} onChange={e => patch('hq_country', e.target.value.toUpperCase())} /></Field>
                <Field label="Postal code"><Input value={form.hq_postal_code} onChange={e => patch('hq_postal_code', e.target.value)} /></Field>
              </div>
            </CardContent></Card>
            <Field label="Tags (comma-separated)">
              <Input
                value={(form.tags || []).join(', ')}
                onChange={e => patch('tags', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              />
            </Field>
          </TabsContent>

          {/* CONTACTS */}
          <TabsContent value="contacts" className="space-y-3">
            {CONTACT_KEYS.map(({ key, label, hint }) => (
              <Card key={key}>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-baseline justify-between">
                    <div className="text-sm font-semibold">{label}</div>
                    <div className="text-[11px] text-gray-500 italic">{hint}</div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Field label="Full name"><Input value={form[key]?.full_name || ''} onChange={e => patchNested(key, 'full_name', e.target.value)} data-testid={`contact-${key}-name`} /></Field>
                    <Field label="Role"><Input value={form[key]?.role || ''} onChange={e => patchNested(key, 'role', e.target.value)} /></Field>
                    <Field label="Email"><Input type="email" value={form[key]?.email || ''} onChange={e => patchNested(key, 'email', e.target.value)} data-testid={`contact-${key}-email`} /></Field>
                    <Field label="Mobile"><Input value={form[key]?.mobile || ''} onChange={e => patchNested(key, 'mobile', e.target.value)} /></Field>
                    <Field label="Landline"><Input value={form[key]?.landline || ''} onChange={e => patchNested(key, 'landline', e.target.value)} /></Field>
                    <Field label="Department"><Input value={form[key]?.department || ''} onChange={e => patchNested(key, 'department', e.target.value)} /></Field>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* BANKING */}
          <TabsContent value="banking" className="space-y-3">
            <BankingBlock title="Where WE pay commissions to them" keyName="commission_payout_details" form={form} patchNested={patchNested} />
            <BankingBlock title="Where THEY pay our invoices" keyName="invoice_receipt_details" form={form} patchNested={patchNested} />
          </TabsContent>

          {/* KYC */}
          <TabsContent value="kyc" className="space-y-3">
            <Card><CardContent className="p-3 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Field label="KYC status">
                  <Select value={form.kyc_status} onValueChange={v => patch('kyc_status', v)}>
                    <SelectTrigger data-testid="kyc-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Insurance certificate URL"><Input value={form.insurance_certificate_url} onChange={e => patch('insurance_certificate_url', e.target.value)} /></Field>
              </div>
              <Field label="KYC notes"><Textarea rows={4} value={form.kyc_notes} onChange={e => patch('kyc_notes', e.target.value)} placeholder="SEC docs, BIR 2303, GIS, etc." /></Field>
              <Field label="Signed contract document URLs (one per line)">
                <Textarea
                  rows={3}
                  value={(form.contract_documents || []).join('\n')}
                  onChange={e => patch('contract_documents', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
                />
              </Field>
            </CardContent></Card>
          </TabsContent>

          {/* COMMERCIAL */}
          <TabsContent value="commercial" className="space-y-3">
            <Card><CardContent className="p-3 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Field label="Billing email"><Input type="email" value={form.billing_email} onChange={e => patch('billing_email', e.target.value)} data-testid="client-billing-email" /></Field>
                <Field label="Billing model">
                  <Select value={form.billing_model} onValueChange={v => patch('billing_model', v)}>
                    <SelectTrigger data-testid="client-billing-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paying_vend">Paying vend (end-user pays)</SelectItem>
                      <SelectItem value="freevend">Freevend (client subsidises)</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                      <SelectItem value="consignment">Consignment</SelectItem>
                      <SelectItem value="operator_owned">Operator-owned</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Credit limit"><Input type="number" step="0.01" value={form.credit_limit ?? 0} onChange={e => patch('credit_limit', parseFloat(e.target.value) || 0)} /></Field>
                <Field label="Payment terms (days)"><Input type="number" value={form.payment_terms_days ?? 30} onChange={e => patch('payment_terms_days', parseInt(e.target.value, 10) || 0)} /></Field>
                <Field label="Preferred currency"><Input maxLength={3} value={form.preferred_currency} onChange={e => patch('preferred_currency', e.target.value.toUpperCase())} /></Field>
              </div>
              <Field label="Internal notes"><Textarea rows={3} value={form.notes} onChange={e => patch('notes', e.target.value)} /></Field>
            </CardContent></Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-between items-center gap-2 pt-3 border-t">
          {!isNew ? (
            <Button variant="ghost" onClick={remove} disabled={busy} className="text-rose-600 hover:text-rose-700 h-9 text-xs" data-testid="client-delete-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy} className="h-9 text-xs sm:text-sm">Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 h-9 text-xs sm:text-sm" data-testid="client-save-btn">
              {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Saving…</> : (isNew ? 'Create client' : 'Save changes')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BankingBlock({ title, keyName, form, patchNested }) {
  const b = form[keyName] || {};
  return (
    <Card>
      <CardContent className="p-3 space-y-2">
        <div className="text-sm font-semibold">{title}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Field label="Account holder"><Input value={b.account_holder || ''} onChange={e => patchNested(keyName, 'account_holder', e.target.value)} /></Field>
          <Field label="Bank name"><Input value={b.bank_name || ''} onChange={e => patchNested(keyName, 'bank_name', e.target.value)} data-testid={`${keyName}-bank-name`} /></Field>
          <Field label="Branch"><Input value={b.branch || ''} onChange={e => patchNested(keyName, 'branch', e.target.value)} /></Field>
          <Field label="Account number"><Input value={b.account_number || ''} onChange={e => patchNested(keyName, 'account_number', e.target.value)} data-testid={`${keyName}-account-number`} /></Field>
          <Field label="SWIFT / BIC"><Input value={b.swift_bic || ''} onChange={e => patchNested(keyName, 'swift_bic', e.target.value)} /></Field>
          <Field label="IBAN"><Input value={b.iban || ''} onChange={e => patchNested(keyName, 'iban', e.target.value)} /></Field>
          <Field label="Routing (US)"><Input value={b.routing_number || ''} onChange={e => patchNested(keyName, 'routing_number', e.target.value)} /></Field>
          <Field label="Sort code (UK)"><Input value={b.sort_code || ''} onChange={e => patchNested(keyName, 'sort_code', e.target.value)} /></Field>
          <Field label="Currency"><Input maxLength={3} value={b.payment_currency || 'PHP'} onChange={e => patchNested(keyName, 'payment_currency', e.target.value.toUpperCase())} /></Field>
          <Field label="Method">
            <Select value={b.payment_method || 'bank_transfer'} onValueChange={v => patchNested(keyName, 'payment_method', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
                <SelectItem value="gcash">GCash</SelectItem>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="wire">Wire</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Their supplier / reference (optional)"><Input value={b.payment_reference || ''} onChange={e => patchNested(keyName, 'payment_reference', e.target.value)} /></Field>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] text-gray-700 font-medium uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}
